import {
  forwardRef,
  useCallback,
  useId,
  useRef,
  type TextareaHTMLAttributes,
  type ChangeEvent,
} from 'react'
import { cn } from '../../utils.js'

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  /** Visible label text */
  label?: string
  /** Helper text shown below the textarea */
  helperText?: string
  /** Error message (also sets aria-invalid) */
  error?: string
  /** Auto-resize as the user types */
  autoResize?: boolean
  /** Show character count when maxLength is set */
  showCount?: boolean
}

/**
 * Accessible textarea with optional auto-resize and character count.
 */
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  {
    label,
    helperText,
    error,
    autoResize = false,
    showCount = false,
    maxLength,
    className,
    id: externalId,
    onChange,
    value,
    defaultValue,
    ...props
  },
  ref,
) {
  const generatedId = useId()
  const id = externalId ?? generatedId
  const helperId = `${id}-helper`
  const errorId = `${id}-error`
  const hasError = Boolean(error)
  const internalRef = useRef<HTMLTextAreaElement | null>(null)

  const handleResize = useCallback((el: HTMLTextAreaElement) => {
    if (autoResize) {
      el.style.height = 'auto'
      el.style.height = `${el.scrollHeight}px`
    }
  }, [autoResize])

  const setRef = useCallback(
    (el: HTMLTextAreaElement | null) => {
      internalRef.current = el
      if (typeof ref === 'function') ref(el)
      else if (ref) (ref as React.MutableRefObject<HTMLTextAreaElement | null>).current = el
      if (el) handleResize(el)
    },
    [ref, handleResize],
  )

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      handleResize(e.currentTarget)
      onChange?.(e)
    },
    [onChange, handleResize],
  )

  const currentLength =
    typeof value === 'string'
      ? value.length
      : typeof defaultValue === 'string'
        ? defaultValue.length
        : 0

  return (
    <div className="flex flex-col gap-1.5">
      {label && (
        <label
          htmlFor={id}
          className="text-sm font-medium text-[var(--color-text)]"
        >
          {label}
        </label>
      )}
      <textarea
        ref={setRef}
        id={id}
        aria-invalid={hasError}
        aria-describedby={
          [error ? errorId : null, helperText ? helperId : null]
            .filter(Boolean)
            .join(' ') || undefined
        }
        maxLength={maxLength}
        value={value}
        defaultValue={defaultValue}
        onChange={handleChange}
        className={cn(
          'w-full rounded-[var(--radius-md)] border bg-[var(--color-background)] text-[var(--color-text)]',
          'placeholder:text-[var(--color-text-muted)]',
          'focus:outline-none focus:ring-2 focus:ring-offset-0',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          'transition-colors px-3 py-2 text-sm min-h-[5rem]',
          autoResize && 'resize-none overflow-hidden',
          hasError
            ? 'border-[var(--color-error)] focus:ring-[var(--color-error)]'
            : 'border-[var(--color-border)] focus:ring-[var(--color-primary)]',
          className,
        )}
        {...props}
      />
      <div className="flex justify-between">
        <div>
          {error && (
            <p id={errorId} role="alert" className="text-sm text-[var(--color-error)]">
              {error}
            </p>
          )}
          {helperText && !error && (
            <p id={helperId} className="text-sm text-[var(--color-text-secondary)]">
              {helperText}
            </p>
          )}
        </div>
        {showCount && maxLength != null && (
          <span className="text-xs text-[var(--color-text-muted)]">
            {currentLength}/{maxLength}
          </span>
        )}
      </div>
    </div>
  )
})
